import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from "@ngrx/signals";
import { isDefined, isUndefined } from "../../../util/core";
import { S25Promise } from "../../../util/core/s25.promise";
import { S25Error } from "../../../util/core/s25.error";
import { S25Array } from "../../../util/core/s25.array";
import { S25Bool } from "../../../util/core/s25.bool";
import { s25Resource } from "../../../util/s25.resource";
import { computed, effect, inject, linkedSignal, signal, untracked } from "@angular/core";
import { EventPricingService, InvoiceCreateData } from "./s25.event.pricing.service";
import { Invoice, InvoiceUpdateData, LineItem, lineItemType, MicroBilling } from "../../../pojo/Pricing";
import { InvoiceMove } from "./invoice/move-item/invoice-move";
import { isDefaultSet, isReferenceSet, SystemSets } from "./invoice/system-sets/system-sets";
import { InvoiceTotals } from "./invoice/totals/invoice-totals";
import { StateStore } from "../../../services/state/state.store";
import { S25States } from "../../../services/state/state.map";
import { FlsStore } from "../../../stores/fls.store";
import { OlsService } from "../../../services/ols.service";
import { Item } from "../../../pojo/Item";
import { AccessLevels, hasEditFls } from "../../../pojo/Fls";
import { EventService } from "../../../services/event.service";
import { UserprefService } from "../../../services/userpref.service";
import { RateService } from "../../../services/rate.service";
import { PricingService } from "../../../services/pricing.service";
import { EventMicroService } from "../../../services/event.micro.service";
import { EventMicroI, ExpandedInfoI, WsDataI, WSReservations } from "../../s25-event/EventMicroI";
import { S25ObjectReservation, S25RmReservation, S25RsReservation } from "../../s25-event/ReservationI";
import { NameService } from "../../../services/name.service";
import { S25Const } from "../../../util/s25-const";
import { TelemetryService } from "../../../services/telemetry.service";
import { Event } from "../../../pojo/Event";
import { AriaLive } from "../../../services/aria.live.service";
import { CacheRepository } from "../../../decorators/cache.decorator";

export type PricingView = "lineItem" | "occurrence";

export type SidebarView = "invoices" | "summary";

export const PRICING_BUILD_POLL_MS = 15_000;
const PRICING_BUILD_MAX_RETRIES = 6;

/**
 * Whether the server has priced the event at all, which is the only thing the build wait waits for.
 * Asked of billing without its rate info, which the pricing page that loads next fetches in full
 * anyway, so this stays the cheaper of the two requests.
 */
async function isPriced(eventIds: number[]): Promise<boolean> {
    const [items] = await S25Promise.Maybe(PricingService.getStandardPricingForEvent(eventIds));
    return !!items?.length;
}

function eventEntries(detail: { content?: WsDataI } | undefined): EventMicroI[] {
    return detail?.content?.data?.items ?? [];
}

export function eventStateOf(events: EventMicroI[], eventId: number | undefined): Event.State.Ids | undefined {
    return events.find((event) => event.id === eventId)?.context?.state;
}

export const PRICING_LOADING_ANNOUNCEMENT = "Loading pricing for this event";
export const PRICING_LOADED_ANNOUNCEMENT = "Pricing for this event is ready";

export const DEFAULT_SET_DISPLAY_NAME = "Standard Invoice";

/**
 * What to call an invoice on screen. The API names the default set after its event, which is a key
 * rather than a title, so it stands in for one until the user gives it a name of their own. Matching
 * the shape rather than this event's id keeps it right when related events are combined.
 */
export function invoiceDisplayName(invoice: Invoice | undefined): string {
    const name = invoice?.billDefn?.billName ?? "";
    if (isDefaultSet(invoice) && SystemSets.DEFAULT_SET_NAME.test(name)) return DEFAULT_SET_DISPLAY_NAME;
    return name;
}

/** An invoice the route names, `none` for a route that names none, `unstated` for one that is silent. */
export type RoutedInvoice = number | "none" | "unstated";

/**
 * What the newest route says about which invoice to open. Route history is pushed when a transition
 * is created rather than when it lands, so an entry for anywhere else says nothing.
 *
 * A link back to a page already visited is silent in the same way: it carries an itemId and nothing
 * else, and the router fills the invoice segment of the URL in from whatever is already open. Such a
 * route asks for no change, which is different from a route that names the page with an empty
 * invoice, as the Pricing tab and a link to the summary both do.
 */
export function pricingRoutedInvoice(stateStore: InstanceType<typeof StateStore>): RoutedInvoice {
    if (stateStore.currentStateName() !== S25States.names.eventPricing) return "unstated";

    const params = stateStore.routeHistory()?.at(-1)?.to?._params;
    if (!params || !("evBillId" in params)) return "unstated";
    const parsed = parseInt(String(params.evBillId));
    return Number.isFinite(parsed) ? parsed : "none";
}

/** The invoice the newest route asks for, undefined when it asks for none or asks for nothing. */
export function pricingRouteEvBillId(stateStore: InstanceType<typeof StateStore>): number | undefined {
    const routed = pricingRoutedInvoice(stateStore);
    return typeof routed === "number" ? routed : undefined;
}

/**
 * Whether the address bar is on this event, which is what makes the URL this page's to correct. The
 * route names the page but not the event it is for: a move between an event's tabs carries no
 * itemId of its own, and the pricing state is sticky, so a page for one event outlives the visit and
 * keeps running while another event's pricing is the one on screen.
 */
export function urlNamesEvent(eventId: number, hash: string = window.location.hash): boolean {
    return new RegExp(`/event/${eventId}(?![0-9])`).test(hash);
}

/**
 * Whether the address bar already reads as `wanted`. The URL is compared rather than the route
 * because the two disagree in both directions: a deep link arrives through a redirect the router
 * writes no address for, leaving the URL a page behind, and a click on the Pricing tab carries the
 * last invoice into the URL without naming it in the route's params.
 */
export function addressBarShows(wanted: string, hash: string = window.location.hash): boolean {
    const path = (url: string) => url.split("?")[0].replace(/\/+$/, "");
    return path(wanted) === path(hash);
}

/**
 * What the invoice form should open as. A create request can carry an item on its way off another
 * invoice, in which case the new invoice takes it over once it exists.
 */
export type InvoiceFormRequest =
    | {
          mode: "create";
          move?: { item: InvoiceMove.MovableItem; sourceInvoiceId: number };
          preselect?: { rsrvIds?: number[]; requirementIds?: number[]; includeEventType?: boolean };
          /** Opens the new invoice once it is created, for a request made from somewhere it isn't. */
          selectOnCreate?: boolean;
      }
    | { mode: "edit"; invoiceId: number };

export type EventRequirement = { requirementId: number; requirementName: string; eventId: number };

/** Everything an occurrence row shows beyond its dates, none of which the billing service knows. */
export type OccurrenceDetail = {
    eventId: number;
    eventName: string;
    eventTitle: string;
    profileName: string;
    profileComment: string;
    comments: string;
    locations: string[];
    resources: string[];
};

type EventWithReservations = { profiles?: { reservations?: unknown[] }[] };

const DENIED_OR_CANCELLED = 3;

/**
 * Location and resource assignments arrive bucketed by how firm they are, and each leaf names only
 * an id, so the names come from the expanded dictionary. Ids missing from it are ones the user has
 * no rights to see, and are left out the way the old pricing set left out private names.
 */
function assignedNames(
    buckets: S25ObjectReservation[] | undefined,
    idOf: (assignment: S25RmReservation | S25RsReservation) => number | undefined,
    names: Map<number, string | undefined>,
): string[] {
    const assigned: string[] = [];

    for (const bucket of buckets ?? []) {
        const assignments = [...(bucket.reserved ?? []), ...(bucket.draft ?? []), ...(bucket.requested ?? [])];
        for (const assignment of assignments) {
            if (assignment.status === DENIED_OR_CANCELLED) continue;
            const id = idOf(assignment);
            const name = id ? names.get(id) : undefined;
            if (name) assigned.push(name);
        }
    }

    return S25Array.unique(assigned);
}

export function buildOccurrenceDetails(
    events: EventMicroI[],
    expandedInfo: ExpandedInfoI | undefined,
): Map<number, OccurrenceDetail> {
    const spaceNames = new Map((expandedInfo?.spaces ?? []).map((space) => [space.spaceId, space.spaceName]));
    const resourceNames = new Map(
        (expandedInfo?.resources ?? []).map((resource) => [resource.resourceId, resource.resourceName]),
    );
    const details = new Map<number, OccurrenceDetail>();

    for (const event of events) {
        if (!event.id) continue;
        for (const profile of event.profiles ?? []) {
            for (const reservation of (profile.reservations ?? []) as WSReservations[]) {
                if (!reservation.rsrvId) continue;
                details.set(reservation.rsrvId, {
                    eventId: event.id,
                    eventName: event.name ?? "",
                    eventTitle: event.title ?? "",
                    profileName: Item.profileName(profile.name ?? "") ?? "",
                    profileComment: profile.comments ?? "",
                    comments: reservation.comments ?? "",
                    locations: assignedNames(
                        reservation.spaces,
                        (space) => (space as S25RmReservation).spaceId,
                        spaceNames,
                    ),
                    resources: assignedNames(
                        reservation.resources,
                        (resource) => (resource as S25RsReservation).resourceId,
                        resourceNames,
                    ),
                });
            }
        }
    }

    return details;
}

/**
 * What each segment is called, by id. Auto-generated rsrv_ names are left out
 * so an unnamed segment contributes nothing to a line item's label.
 */
export function buildProfileNames(events: EventMicroI[]): Map<number, string> {
    const names = new Map<number, string>();

    for (const event of events) {
        for (const profile of event.profiles ?? []) {
            const name = Item.profileName(profile.name ?? "");
            if (profile.profileId && name) names.set(profile.profileId, name);
        }
    }

    return names;
}

/** What a line item is called on screen: its own name, and the segment it bills. */
export function lineItemLabel(itemName: string | undefined, profileName: string | undefined): string {
    if (!profileName) return itemName ?? "";
    return `${itemName ?? ""} (${profileName})`;
}

/**
 * The invoice list with every invoice a response describes swapped in. A write only answers with
 * the invoices its request named, so anything it leaves out is kept as it stood.
 */
export function invoicesWithResponse(
    current: Invoice[],
    response: MicroBilling | undefined,
    eventId: number | undefined,
): Invoice[] {
    const returned = response?.data?.items ?? [];
    if (!returned.length) return current;

    const returnedById = new Map(returned.map((invoice) => [invoice.id, invoice]));
    const currentIds = new Set(current.map((invoice) => invoice.id));
    const merged = [
        ...current.map((invoice) => returnedById.get(invoice.id) ?? invoice),
        ...returned.filter((invoice) => !currentIds.has(invoice.id)),
    ];

    // Each event numbers its own invoices, so an order that leaves any of them out cannot be applied
    // to the list as a whole - the ones it skips would all land together at the front.
    const sortOrderById = new Map(
        (response?.sort?.find((group) => group.eventId === eventId)?.sortGroup ?? []).map(({ evBillId, sortOrder }) => [
            evBillId,
            sortOrder,
        ]),
    );
    if (merged.some((invoice) => !sortOrderById.has(invoice.id))) return merged;

    return merged.sort((first, second) => sortOrderById.get(first.id)! - sortOrderById.get(second.id)!);
}

/** The invoices a request sets out to change, whether it names them per entry or per line item. */
function billIds(entries: { billId?: number }[]): number[] {
    return [...new Set(entries.map((entry) => entry.billId).filter((billId): billId is number => !!billId))];
}

/** Whether the response accounts for every invoice the request set out to change. */
export function describesInvoices(response: MicroBilling | undefined, invoiceIds: number[]): boolean {
    const described = new Set(
        (response?.data?.items ?? []).filter((invoice) => !!invoice.billDefn).map((invoice) => invoice.id),
    );
    return invoiceIds.every((invoiceId) => described.has(invoiceId));
}

/**
 * How the server states a change it would not make: validation drops the offending invoice from the
 * response and answers 200 regardless, leaving this the only account of what happened.
 */
export function refusalMessage(response: MicroBilling | undefined): string {
    return S25Array.forceArray(response?.messages)
        .map((message) => message?.message)
        .filter(Boolean)
        .join(" ");
}

/** The detail endpoint sends requirement ids only, so the names come from the expanded dictionary. */
export function buildEventRequirements(
    events: EventMicroI[],
    expandedInfo: ExpandedInfoI | undefined,
): EventRequirement[] {
    const names = new Map(
        (expandedInfo?.requirements ?? []).map((requirement) => [
            Number(requirement.requirementId),
            requirement.requirementName,
        ]),
    );

    return events.flatMap((event) =>
        (event.requirements ?? []).flatMap((requirement) => {
            const requirementId = Number(requirement.requirementId);
            if (!event.id || !Number.isFinite(requirementId)) return [];
            return [
                {
                    requirementId,
                    requirementName: names.get(requirementId) || S25Const.private,
                    eventId: event.id,
                },
            ];
        }),
    );
}

export function mergeOccurrenceDates(
    billingOccurrences: WSReservations[],
    events: EventWithReservations[],
): Record<number, WSReservations> {
    const map: Record<number, WSReservations> = {};

    for (const occurrence of billingOccurrences) {
        if (occurrence.rsrvId) map[occurrence.rsrvId] = occurrence;
    }

    for (const event of events) {
        for (const profile of event.profiles ?? []) {
            for (const reservation of (profile.reservations ?? []) as WSReservations[]) {
                const rsrvId = reservation.rsrvId;
                if (!rsrvId) continue;
                map[rsrvId] = {
                    ...map[rsrvId],
                    ...reservation,
                    occurrence: map[rsrvId]?.occurrence ?? reservation.occurrence,
                };
            }
        }
    }

    return map;
}

type State = {
    eventId: number | undefined;
    combineRelated: boolean;
    hiddenColumnIds: string[];
    hideNoChargeItems: boolean;
    pricingView: PricingView;
    /** The tab is hidden rather than unmounted while another of the event's tabs is open. */
    tabVisible: boolean;
    /**
     * Set when the store stands in for the pricing page only so an email compose panel has an invoice to
     * write about, which is how the organization's Invoices tab opens one. The page's own reads -
     * the event, its rate groups, its access level, whether it needs repricing - have no reader
     * there, so they are not made.
     */
    composeOnly: boolean;
};

export const PricingStore = signalStore(
    withState<State>({
        eventId: undefined,
        combineRelated: false,
        hiddenColumnIds: [],
        hideNoChargeItems: false,
        pricingView: "lineItem",
        tabVisible: true,
        composeOnly: false,
    }),
    withProps(({ eventId, composeOnly }) => ({
        _pricingService: inject(EventPricingService),
        _stateStore: inject(StateStore),
        _flsStore: inject(FlsStore),
        /** Set while the create/edit invoice form is open, and the request it opened with. */
        invoiceFormRequest: signal<InvoiceFormRequest | undefined>(undefined),
        /** Set while the first load is waiting on the server to finish pricing a new event. */
        pricingBuilding: signal(false),
        _waitedForPricing: signal(false),
        /** Set once a write has updated billing affecting the system sets */
        _systemSetsStale: signal(false),
        /** Set by a state change that arrived while the tab was hidden, spent on coming back. */
        _eventStateChangePending: signal(false),
        /** Set while a state change is being taken up, which refetches the event and its billing. */
        reloadingForEventState: signal(false),
        _relatedEventIds: s25Resource({
            if: () => !!eventId() && !composeOnly(),
            params: () => eventId(),
            loader: async ({ params: id }) => {
                if (isUndefined(id)) return [];
                return EventService.getRelatedEventIds(id);
            },
        }),
        _dateFormat: s25Resource({ loader: () => UserprefService.getS25Dateformat() }),
        _timeFormat: s25Resource({ loader: () => UserprefService.getS25Timeformat() }),
        _dateTimeFormat: s25Resource({ loader: () => UserprefService.getS25DateTimeformat() }),
        _ols: s25Resource({
            if: () => !!eventId() && !composeOnly(),
            params: () => eventId(),
            loader: async ({ params: eventId }) => {
                if (isUndefined(eventId)) return;
                return OlsService.getOls([eventId], Item.Ids.Event, "edit");
            },
        }),
        _rateGroups: s25Resource({
            if: () => !composeOnly(),
            loader: async () => RateService.getRateGroups(),
        }),
    })),
    withComputed((store) => ({
        eventIds: () => {
            const id = store.eventId(),
                relatedIds = store.combineRelated() ? (store._relatedEventIds.value() ?? []) : [],
                ids = isDefined(id) ? [id, ...relatedIds] : relatedIds;
            return [...new Set(ids)];
        },
    })),
    withProps((store) => {
        const events = s25Resource({
            if: () => !!store.eventIds().length && !store.composeOnly(),
            params: () => store.eventIds(),
            loader: async ({ params: eventIds }) =>
                EventMicroService.getEventDetailById(eventIds, true, [
                    "context",
                    "organizations",
                    "reservations",
                    "requirements",
                ]),
        });

        const billing = s25Resource({
            if: () => store.eventIds().length > 0,
            params: () => store.eventIds(),
            loader: async ({ params: eventIds }) => {
                // Nothing to bill has three causes: a server that does not bill with reference
                // sets, where an event with no invoices of its own has none at all; a Draft event,
                // which the server does not price; and ANG-6835, where the server prices an event a
                // moment after creating it and someone arrives from the event form before it has.
                // Only the last is worth waiting for. Only the first load waits - later ones answer
                // empty because the user emptied it.
                const settled = async (answer: MicroBilling | undefined) =>
                    !!answer?.data?.items?.length || (await isPriced(eventIds));

                let invoices = await store._pricingService.getInvoices(eventIds);
                if (store._waitedForPricing() || (await settled(invoices))) return invoices;

                await events.awaitLoad();
                if (eventStateOf(eventEntries(events.lastValue()), store.eventId()) === Event.State.Ids.Draft) {
                    return invoices;
                }

                store._waitedForPricing.set(true);

                store.pricingBuilding.set(true);
                try {
                    for (let attempt = 0; attempt < PRICING_BUILD_MAX_RETRIES; attempt++) {
                        await S25Promise.delay(PRICING_BUILD_POLL_MS);
                        invoices = await store._pricingService.getInvoices(eventIds);
                        if (await settled(invoices)) break;
                    }
                    return invoices;
                } finally {
                    store.pricingBuilding.set(false);
                }
            },
        });

        return {
            _events: events,
            _billing: billing,
            _eventsNeedingRefresh: s25Resource({
                if: () => !!store.eventIds().length && !store.composeOnly(),
                params: () => store.eventIds(),
                loader: async ({ params: eventIds }) => EventService.getEventsNeedingRefresh(eventIds),
            }),
            // lastValue rather than value: a params change (eg toggling combine related) clears the resource value,
            // which would unmount the invoice view mid-refetch
            invoices: linkedSignal(() => billing.lastValue()?.data?.items ?? []),
        };
    }),
    withComputed((store) => {
        const listedInvoices = (): Invoice[] => store.invoices().filter((invoice) => !isReferenceSet(invoice));

        return {
            listedInvoices,
            /**
             * The invoices the sidebar lists, with every event's default set folded into the priced
             * event's. Memoized, because the folded set is built here and everything that compares
             * invoices has to be handed the same one.
             */
            displayedInvoices: computed((): Invoice[] => SystemSets.foldDefaultSets(listedInvoices(), store.eventId())),
            referenceInvoice: computed((): Invoice | undefined =>
                SystemSets.mergeAll(store.invoices().filter(isReferenceSet), store.eventId()),
            ),
            defaultSetIdByEvent: (): ReadonlyMap<number, number> => {
                const byEvent = new Map<number, number>();

                for (const invoice of listedInvoices()) {
                    if (!isDefaultSet(invoice)) continue;
                    const eventId = SystemSets.eventIdOf(invoice);
                    if (isDefined(eventId)) byEvent.set(eventId, invoice.id);
                }
                return byEvent;
            },
            invoiceById: (): ReadonlyMap<number, Invoice> =>
                new Map(listedInvoices().map((invoice): [number, Invoice] => [invoice.id, invoice])),
            usesReferenceSets: (): boolean =>
                store.invoices().some((invoice) => isReferenceSet(invoice) || isDefaultSet(invoice)),
        };
    }),
    withProps((store) => ({
        /**
         * The invoice on screen. Seeded from the route so a link into a particular invoice opens it,
         * but only from a route that is this page: every transition anywhere in the app pushes route
         * history, and re-reading a route with no evBillId would silently reselect the first invoice
         * and race a URL update against the navigation the user just started.
         */
        evBillId: linkedSignal<{ routeId: number | undefined; invoices: Invoice[] }, number | undefined>({
            source: () => ({
                routeId: pricingRouteEvBillId(store._stateStore),
                invoices: store.displayedInvoices(),
            }),
            computation: ({ routeId, invoices }, previous) => {
                const listed = (id: number | undefined) => !!id && invoices.some((invoice) => invoice.id === id);
                if (listed(routeId)) return routeId;
                if (listed(previous?.value)) return previous!.value;
                return invoices[0]?.id;
            },
        }),
        /**
         * A link that names one of this event's invoices opens it. Anything else opens the summary:
         * arriving at the page without an invoice, which is how the event's Pricing tab links here,
         * and moving between events, which carries the old event's invoice along in the params.
         */
        sidebarView: linkedSignal<{ routed: RoutedInvoice; invoices: Invoice[] }, SidebarView>({
            source: () => ({
                routed: pricingRoutedInvoice(store._stateStore),
                invoices: store.displayedInvoices(),
            }),
            computation: ({ routed, invoices }, previous) => {
                // A route with nothing to say leaves the page as the user left it, which is what
                // makes an invoice still be open on coming back to a page already visited.
                if (routed === "unstated") return previous?.value ?? "summary";
                return invoices.some((invoice) => invoice.id === routed) ? "invoices" : "summary";
            },
        }),
        organizations: linkedSignal(
            () =>
                store._events.value()?.content?.expandedInfo?.organizations?.map((org) => ({
                    itemName: org.organizationName,
                    itemId: org.organizationId,
                })) ?? [],
        ),
    })),
    withComputed((store) => {
        const billingOrgNameById = computed(
            () =>
                new Map(
                    (store._billing.lastValue()?.expandedInfo?.organizations ?? []).map((org) => [
                        org.organizationId,
                        org.organizationName,
                    ]),
                ),
        );
        const eventOrgIds = computed(() => new Set(store.organizations().map((org) => Number(org.itemId))));
        const referencedBilledOrgIds = computed(() => {
            const orgIds = new Set<number>();
            for (const invoice of store.invoices()) {
                for (const account of invoice.billing?.subtotals?.[0]?.account ?? []) {
                    const chargeToId = account.chargeToId;
                    if (chargeToId && Number.isFinite(chargeToId)) orgIds.add(chargeToId);
                }
                for (const payment of invoice.payments ?? []) {
                    const organizationId = payment.organizationId;
                    if (organizationId && Number.isFinite(organizationId)) orgIds.add(organizationId);
                }
            }
            return orgIds;
        });
        const unlistedBilledOrgIds = computed(() =>
            [...referencedBilledOrgIds()].filter((orgId) => !eventOrgIds().has(orgId)),
        );

        const orgIdsNeedingFetch = computed(() =>
            unlistedBilledOrgIds()
                .filter((orgId) => !billingOrgNameById().has(orgId))
                .sort((first, second) => first - second),
        );
        return {
            _billingOrgNameById: billingOrgNameById,
            _unlistedBilledOrgIds: unlistedBilledOrgIds,
            _orgIdsNeedingFetch: orgIdsNeedingFetch,
        };
    }),
    withProps((store) => ({
        _fetchedOrgNames: s25Resource({
            if: () => store._orgIdsNeedingFetch().length > 0,
            params: () => store._orgIdsNeedingFetch(),
            loader: async ({ params: orgIds }) => {
                const nameMap = await NameService.getNames(
                    orgIds.map((orgId) => ({ itemId: orgId, itemTypeId: Item.Ids.Organization })),
                );
                return new Map(
                    Object.entries(nameMap ?? {}).map(([idHash, orgName]) => [
                        Number(idHash?.split("-")?.[0]),
                        orgName,
                    ]),
                );
            },
        }),
    })),
    withComputed((store) => ({
        unlistedBilledOrgs: (): { itemId: number; itemName: string }[] => {
            const billingNames = store._billingOrgNameById();
            const fetchedNames = store._fetchedOrgNames.value();
            return store._unlistedBilledOrgIds().map((orgId) => ({
                itemId: orgId,
                itemName: billingNames.get(orgId) ?? fetchedNames?.get(orgId) ?? S25Const.private,
            }));
        },
    })),
    withMethods((store) => {
        const reloadInvoices = () => {
            store._systemSetsStale.set(false);
            return store._billing.reloadAndAwait();
        };

        // Only the signal is written here: S25EventPricingComponent owns the URL, and navigating from
        // both places raced two identical transitions, the loser of which logged a rejection.
        const setEvBillId = (id: number | undefined) => {
            store.evBillId.set(id);
        };

        const mergeInvoiceResponse = (response: MicroBilling | undefined) => {
            store.invoices.set(invoicesWithResponse(store.invoices(), response, store.eventId()));
            // A write answers with the invoices it named, never the system sets, so the reference
            // set behind billed items and the default set holding what no invoice claims are both
            // behind now. Only the server can restate them.
            store._systemSetsStale.set(true);
        };

        /**
         * Takes the invoices a write answered with, or goes back to the server when it left any of
         * them out - which is how a refused change arrives, the reason for it in `messages`.
         */
        const mergeOrReload = async (
            response: MicroBilling | undefined,
            expectedInvoiceIds: number[],
        ): Promise<{ error?: string }> => {
            if (describesInvoices(response, expectedInvoiceIds)) {
                mergeInvoiceResponse(response);
                return {};
            }

            await reloadInvoices();
            return { error: refusalMessage(response) || undefined };
        };

        const updateInvoice = async (
            updateData: Partial<InvoiceUpdateData>,
            invoiceId: number,
            options?: { deferMerge?: boolean },
        ): Promise<{ error?: string; applyMerge?: () => void }> => {
            const [response, error] = await S25Promise.Maybe(
                store._pricingService.putBillingCustom(updateData, invoiceId),
            );

            if (error) return { error: S25Error.extract(error, S25Const.defaultErrorMessage) };

            if (!describesInvoices(response?.content, [invoiceId])) {
                await reloadInvoices();
                return { error: refusalMessage(response?.content) || undefined };
            }

            const applyMerge = () => mergeInvoiceResponse(response.content);
            if (options?.deferMerge) return { applyMerge };

            applyMerge();
            return {};
        };

        return {
            setEventId(id: number | string) {
                if (isUndefined(id)) return;
                patchState(store, { eventId: parseInt(id) });
            },
            setComposeOnly(composeOnly: boolean) {
                patchState(store, { composeOnly });
            },
            setEvBillId,
            applySortOrder(orderedIds: number[]) {
                const place = new Map(orderedIds.map((id, index): [number, number] => [id, index]));
                const sorted = store
                    .invoices()
                    .filter((invoice) => place.has(invoice.id))
                    .sort((first, second) => place.get(first.id)! - place.get(second.id)!);

                let next = 0;
                store.invoices.set(
                    store.invoices().map((invoice) => (place.has(invoice.id) ? sorted[next++] : invoice)),
                );
            },
            openInvoiceForm(request: InvoiceFormRequest) {
                store.invoiceFormRequest.set(request);
            },
            closeInvoiceForm() {
                store.invoiceFormRequest.set(undefined);
            },
            setCombineRelated(combine: boolean) {
                patchState(store, { combineRelated: combine });
            },
            setHiddenColumns(ids: string[]) {
                patchState(store, { hiddenColumnIds: ids });
            },
            setHideNoChargeItems(hide: boolean) {
                patchState(store, { hideNoChargeItems: hide });
            },
            setPricingView(view: PricingView) {
                patchState(store, { pricingView: view });
                TelemetryService.sendWithSub("Pricing", "Event", view === "occurrence" ? "OccView" : "InlineView");
            },
            setSidebarView(view: SidebarView) {
                store.sidebarView.set(view);
                // Billed items report the reference set as the server last stated it, so opening the
                // summary after a write has to go back for it.
                if (view === "summary" && store._systemSetsStale()) void reloadInvoices();
                TelemetryService.sendWithSub(
                    "Pricing",
                    "Sidebar",
                    view === "summary" ? "InvoiceSummaryView" : "InvoiceListView",
                );
            },
            updateInvoiceName(invoiceId: number, newName: string) {
                const updated = store.invoices().map((invoice) => {
                    if (invoice.id === invoiceId) {
                        return { ...invoice, billDefn: { ...invoice.billDefn, billName: newName } };
                    }
                    return invoice;
                });
                store.invoices.set(updated);
            },
            mergeInvoiceResponse,
            reloadInvoices,
            /** Saves one org's invoice number, then merges the invoice the server reports it on. */
            async updateInvoiceNumber(orgId: number, invoiceNumber: string): Promise<{ error?: string }> {
                const billId = store.evBillId();
                if (!billId) return { error: S25Const.defaultErrorMessage };

                return updateInvoice({ invoiceDetails: [{ billId, acctId: orgId, invoiceId: invoiceNumber }] }, billId);
            },
            updateInvoice,
            async updateRateGroup(args: {
                invoiceId: number;
                rateGroupId: number;
                lineItem?: LineItem;
            }): Promise<{ error?: string }> {
                const { invoiceId, rateGroupId, lineItem } = args;
                const change: Partial<InvoiceUpdateData> = lineItem
                    ? {
                          op: "update",
                          lineItems: [
                              {
                                  billId: invoiceId,
                                  profileId: lineItem.profileId,
                                  itemId: lineItem.itemId,
                                  itemType: lineItem.itemType,
                                  rateGroupId,
                              },
                          ],
                      }
                    : { op: "all", fields: [{ billId: invoiceId, rateGroupId }] };

                const [response, error] = await S25Promise.Maybe(
                    store._pricingService.putBillingCustomBatch([change, { billIdList: [invoiceId] }]),
                );
                if (error) return { error: S25Error.extract(error, S25Const.defaultErrorMessage) };

                return mergeOrReload(response?.content, [invoiceId]);
            },
            /**
             * Creates an invoice, then hands it whatever the request brought along: an item taken off
             * another invoice, and adjustments reallocated to the invoice that only now has an id.
             */
            async createInvoice(args: {
                createData: InvoiceCreateData;
                move?: { item: InvoiceMove.MovableItem; sourceInvoiceId: number };
                adjustments?: LineItem[];
            }): Promise<{ error?: string; invoiceId?: number }> {
                TelemetryService.sendWithSub("Pricing", "Event", "CreateSet");

                const [response, createError] = await S25Promise.Maybe(
                    store._pricingService.postBillingCustom(args.createData),
                );
                if (createError) return { error: S25Error.extract(createError, S25Const.defaultErrorMessage) };

                const invoiceId = response?.content?.data?.items?.[0]?.id;
                if (!invoiceId) {
                    await reloadInvoices();
                    return { error: refusalMessage(response?.content) || undefined };
                }

                const { error: createRefusal } = await mergeOrReload(response?.content, [invoiceId]);
                if (createRefusal) return { invoiceId, error: createRefusal };

                const source = args.move
                    ? store.listedInvoices().find((invoice) => invoice.id === args.move!.sourceInvoiceId)
                    : undefined;
                if (args.move && source) {
                    const movePayloads = InvoiceMove.buildMovePayloads({ item: args.move.item, source });
                    const [moveResponse, moveError] = await S25Promise.Maybe(
                        store._pricingService.putBillingCustomBatch(movePayloads),
                    );
                    if (moveError) {
                        await reloadInvoices();
                        return { invoiceId, error: S25Error.extract(moveError, S25Const.defaultErrorMessage) };
                    }

                    const { error: moveRefusal } = await mergeOrReload(moveResponse?.content, billIds(movePayloads));
                    if (moveRefusal) return { invoiceId, error: moveRefusal };
                }

                if (args.adjustments?.length) {
                    const lineItems = args.adjustments.map((adjustment) => ({
                        ...adjustment,
                        billId: adjustment.billId ?? invoiceId,
                    }));
                    const [adjustmentResponse, adjustmentError] = await S25Promise.Maybe(
                        store._pricingService.putBillingCustomBatch([{ op: "update", lineItems }]),
                    );
                    if (adjustmentError) {
                        await reloadInvoices();
                        return { invoiceId, error: S25Error.extract(adjustmentError, S25Const.defaultErrorMessage) };
                    }

                    const { error: adjustmentRefusal } = await mergeOrReload(
                        adjustmentResponse?.content,
                        billIds(lineItems),
                    );
                    if (adjustmentRefusal) return { invoiceId, error: adjustmentRefusal };
                }

                setEvBillId(invoiceId);
                return { invoiceId };
            },
            /** Removes an invoice outright. Dropping it moves evBillId to whichever is left at the top. */
            async deleteInvoice(invoiceId: number): Promise<{ error?: string }> {
                TelemetryService.sendWithSub("Pricing", "Event", "SetDelete");

                const [, error] = await S25Promise.Maybe(store._pricingService.deleteInvoice(invoiceId));
                if (error) return { error: S25Error.extract(error, S25Const.defaultErrorMessage) };

                store.invoices.set(store.invoices().filter((invoice) => invoice.id !== invoiceId));
                store._systemSetsStale.set(true);
                return {};
            },
            /**
             * Moves an occurrence or requirement to another invoice, or off its invoice entirely
             * when no target is given. Reallocated line item adjustments go in a second request,
             * since they can only land once the line items exist on the target.
             */
            async moveInvoiceItem(args: {
                item: InvoiceMove.MovableItem;
                sourceInvoiceId: number;
                targetInvoiceId?: number;
                adjustments?: LineItem[];
            }): Promise<{ error?: string }> {
                const invoices = store.listedInvoices(),
                    source = invoices.find((invoice) => invoice.id === args.sourceInvoiceId),
                    target = invoices.find((invoice) => invoice.id === args.targetInvoiceId);

                if (!source || (isDefined(args.targetInvoiceId) && !target)) {
                    return { error: S25Const.defaultErrorMessage };
                }

                const payloads = InvoiceMove.buildMovePayloads({ item: args.item, source, target });

                const [moveResponse, moveError] = await S25Promise.Maybe(
                    store._pricingService.putBillingCustomBatch(payloads),
                );
                if (moveError) return { error: S25Error.extract(moveError, S25Const.defaultErrorMessage) };

                const { error: moveRefusal } = await mergeOrReload(moveResponse?.content, billIds(payloads));
                if (moveRefusal) return { error: moveRefusal };

                if (args.adjustments?.length) {
                    const [adjustmentResponse, adjustmentError] = await S25Promise.Maybe(
                        store._pricingService.putBillingCustomBatch([{ op: "update", lineItems: args.adjustments }]),
                    );
                    if (adjustmentError) {
                        await reloadInvoices();
                        return { error: S25Error.extract(adjustmentError, S25Const.defaultErrorMessage) };
                    }

                    const { error: adjustmentRefusal } = await mergeOrReload(
                        adjustmentResponse?.content,
                        billIds(args.adjustments),
                    );
                    if (adjustmentRefusal) return { error: adjustmentRefusal };
                }

                return {};
            },
            reloadEventPricing() {
                store._eventsNeedingRefresh.resource.reload();
            },
        };
    }),
    withComputed((store) => ({
        hiddenColumns: (): ReadonlySet<string> => new Set(store.hiddenColumnIds()),
        selectedInvoice: () => store.displayedInvoices().find((invoice) => invoice.id === store.evBillId()),
        canEditPricing: () => {
            const ols = store._ols.value()?.[0]?.access_level;
            return (
                store._flsStore.hasFull("EVENT_BILLING") &&
                store._flsStore.hasAtLeast("CU_RATES", AccessLevels.Read) &&
                isDefined(ols) &&
                hasEditFls(ols)
            );
        },
        fullRatePerm: () => store._flsStore.hasFull("ML_PRICE"),
        canEditInvoiceNumber: () => store._flsStore.hasAtLeast("MANAGE_PAY", AccessLevels.Read),
        dateFormats: () => {
            if (!store._dateFormat.value() || !store._timeFormat.value() || !store._dateTimeFormat.value())
                return undefined;
            return {
                date: store._dateFormat.value(),
                time: store._timeFormat.value(),
                dateTime: store._dateTimeFormat.value(),
            };
        },
        allRateGroups: () =>
            store._rateGroups.value()?.map((group) => ({
                itemId: group.rate_group_id,
                itemName: group.rate_group_name,
                isActive: S25Bool.toBool(group.defn_state),
            })) ?? [],
        rateScheduleNameMap: (): Record<number, string> =>
            store._billing
                .value()
                ?.expandedInfo?.rateSchedules?.reduce(
                    (map, { rateId, rateName }) => ({ ...map, [Number(rateId)]: rateName ?? "" }),
                    {},
                ) ?? {},
        occurrences: (): Record<number, WSReservations> =>
            mergeOccurrenceDates(
                store._billing.value()?.expandedInfo?.occurrences ?? [],
                store._events.value()?.content?.data?.items ?? [],
            ),
        occurrenceDetails: (): Map<number, OccurrenceDetail> =>
            buildOccurrenceDetails(
                store._events.value()?.content?.data?.items ?? [],
                store._events.value()?.content?.expandedInfo,
            ),
        profileNameById: (): Map<number, string> =>
            buildProfileNames(store._events.value()?.content?.data?.items ?? []),
        /** Whether any event is segmented, which is what makes the segment of an occurrence worth showing. */
        hasMultipleProfiles: (): boolean =>
            (store._events.value()?.content?.data?.items ?? []).some((event) => (event.profiles?.length ?? 0) > 1),
        eventRequirements: (): EventRequirement[] =>
            buildEventRequirements(
                store._events.value()?.content?.data?.items ?? [],
                store._events.value()?.content?.expandedInfo,
            ),
        eventsNeedingRefresh: () => store._eventsNeedingRefresh.value() ?? [],
        invoicesLoading: () => store._billing.resource.isLoading(),
        invoicesLoaded: () => store._billing.lastValue() !== undefined,
        invoiceError: () => store._billing.resource.error(),
        eventLoaded: () => store._events.lastValue() !== undefined || !!store._events.error(),
        eventState: () => eventStateOf(eventEntries(store._events.lastValue()), store.eventId()),
        /** The server answered, but with nothing to bill, even after waiting for it to finish pricing. */
        pricingUnavailable: () => store._billing.lastValue() !== undefined && !store.invoices().length,
        hasRelatedEvents: () => (store._relatedEventIds.value()?.length ?? 0) > 0,
        relatedEventsResolved: () => !!store._relatedEventIds.value() || !!store._relatedEventIds.error(),
        eventLocatorMap: () =>
            store._events
                .value()
                ?.content?.data?.items?.reduce(
                    (map, { id, eventLocator }) => ({ ...map, [Number(id)]: eventLocator }),
                    {},
                ) ?? {},
        orgNameById: (): Map<number, string | undefined> =>
            new Map(
                [...store.organizations(), ...store.unlistedBilledOrgs()].map((org): [number, string | undefined] => [
                    Number(org.itemId),
                    org.itemName,
                ]),
            ),
    })),
    withComputed((store) => ({
        occurrenceEventIds: (): Map<number, number> =>
            new Map([...store.occurrenceDetails()].map(([rsrvId, detail]) => [rsrvId, detail.eventId])),
        invoiceNumberByOrg: (): ReadonlyMap<number, string> =>
            InvoiceTotals.invoiceNumbersByOrg(store.selectedInvoice()),
        selectedInvoiceOccIds: (): Set<number> => {
            const eventId = store.eventId();
            const combineRelated = store.combineRelated();
            return new Set(
                store
                    .selectedInvoice()
                    ?.billing?.lineItems?.filter(
                        (item) => item.itemType !== lineItemType.EVENT && (combineRelated || item.eventId === eventId),
                    )
                    ?.flatMap((item) => item.occurrences?.map((occ) => occ.rsrvId) ?? []) ?? [],
            );
        },
        // Requirements are the only line item type without occurrences, so anything else billed
        // means the occurrence view has rows to show.
        selectedInvoiceHasOccurrences: (): boolean => {
            const eventId = store.eventId(),
                combineRelated = store.combineRelated();
            return !!store
                .selectedInvoice()
                ?.billing?.lineItems?.some(
                    (item) => !!item.occurrences?.length && (combineRelated || item.eventId === eventId),
                );
        },
    })),
    withComputed((store) => ({
        isOccurrenceView: () => store.pricingView() === "occurrence" && store.selectedInvoiceHasOccurrences(),
        noPricing: () =>
            store.eventLoaded() &&
            store.invoicesLoaded() &&
            store.eventState() === Event.State.Ids.Draft &&
            !store.invoices().length,
    })),
    withMethods((store) => {
        const reloadForEventState = async (): Promise<void> => {
            store._eventStateChangePending.set(false);
            const announce = store.noPricing();
            if (announce) void AriaLive.announce(PRICING_LOADING_ANNOUNCEMENT);

            CacheRepository.invalidateByService("EventMicroService.getEventDetailById", String(store.eventId() ?? ""));

            store.reloadingForEventState.set(true);
            try {
                await Promise.all([store._events.reloadAndAwait(), store._billing.reloadAndAwait()]);
            } finally {
                store.reloadingForEventState.set(false);
            }

            if (announce) void AriaLive.announce(PRICING_LOADED_ANNOUNCEMENT);
        };

        return {
            reloadForEventState,
            setTabVisible(visible: boolean) {
                patchState(store, { tabVisible: visible });
            },
            noteEventStateChanged() {
                if (!store.tabVisible()) return store._eventStateChangePending.set(true);
                void reloadForEventState();
            },
        };
    }),
    withHooks((store) => ({
        onInit() {
            // reload to reflect up-to-date default set when updates affecting it have been made
            effect(() => {
                store.evBillId();
                untracked(() => {
                    if (isDefaultSet(store.selectedInvoice()) && store._systemSetsStale()) {
                        void store.reloadInvoices();
                    }
                });
            });

            // A state change that arrived while another tab was open is spent on coming back.
            effect(() => {
                if (!store.tabVisible() || !store._eventStateChangePending()) return;
                untracked(() => {
                    void store.reloadForEventState();
                });
            });
        },
    })),
);
